import i18n from 'scripts/i18n/i18n';
import escapeHtml from 'scripts/lib/escape_html';
import { CALLOUT_KINDS } from 'components/customer/team_assist/lib/sanitize_team_assist_html';
import TeamAssistCitation from 'models/team_assist_citation';

// A bracket holding only citation handles: `[KB1]`, `[SRC2]`, `[KB1, SRC2]`.
// Model text can hold long runs of whitespace, and of unclosed handles, so
// every run here matches one way only: a pattern that could split a run
// between two quantifiers rescans it from each position, and slows to seconds.
const HANDLE_GROUP = /\[\s*(?:KB|SRC)\d+(?:(?:\s*,\s*|\s+)(?:KB|SRC)\d+)*\s*(?:,\s*)?\]/;
// The spaces before a handle go with it, unless a word follows that would then
// run into the one before. Line breaks stay. The lookbehind starts that run of
// spaces at its beginning only, for the same reason.
const HANDLE_GROUPS = new RegExp(`(?<![ \\t])[ \\t]*${HANDLE_GROUP.source}(?!\\w)|${HANDLE_GROUP.source}`, 'g');

/**
 * Answer or draft text without the model's citation handles. glados collapses
 * two handles that point at one source into one citation, so the client can't
 * number handles to match the Sources list; the list stands on its own.
 */
export function stripCitationHandles(text) {
  return (text || '').replace(HANDLE_GROUPS, '');
}

/**
 * The model's `<callout kind="…">` tags as the element the panel styles: a
 * known kind becomes `<div data-callout="kind">` opening with the panel's own
 * label for it ("Recommendation:"). An unknown kind, and any callout in a draft
 * (drafts go to the customer), keeps its text without the tag. An unclosed
 * callout stays open; the HTML parser closes it, so it streams.
 */
export function renderCallouts(text, { inDraft = false } = {}) {
  const open = [];
  return (text || '').replace(CALLOUT_TAGS, (tag, attributes) => {
    if (tag.startsWith('</')) {
      return open.pop() ? '</div>' : '';
    }
    const kind = calloutKind(attributes);
    const styled = !inDraft && kind !== null;
    open.push(styled);
    return styled ? `<div data-callout="${kind}"><strong>${escapeHtml(calloutLabel(kind))}</strong> ` : '';
  });
}

const CALLOUT_TAGS = /<callout\b([^>]*)>|<\/callout\s*>/gi;

function calloutLabel(kind) {
  return kind === 'warning' ? i18n.t('Warning:') : i18n.t('Recommendation:');
}

function calloutKind(attributes) {
  const match = /\bkind\s*=\s*["']?([a-z]+)/i.exec(attributes || '');
  const kind = match ? match[1].toLowerCase() : null;
  return CALLOUT_KINDS.has(kind) ? kind : null;
}

/**
 * A glados citation as the V1 citation model the shared Sources list renders.
 * glados sends `{ type: 'answer', name, answerId }`, `{ type: 'document', name,
 * excerpt?, location? }`, or `{ type: 'product' | 'web_page', name, url?,
 * excerpt? }`.
 */
export function toTeamAssistCitation(citation) {
  if (!citation || citation.type === 'answer' || !citation.type) {
    return TeamAssistCitation.fromJs({
      type: 'answer',
      snippetId: citation && citation.answerId,
      snippetName: citation && citation.name,
    });
  }
  return TeamAssistCitation.fromJs({
    type: citation.type,
    label: citation.name,
    excerpt: citation.excerpt,
    location: citation.location,
    url: citation.url,
  });
}
